import {
  Directive,
  ElementRef,
  Renderer2,
  RendererStyleFlags2,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Provider } from 'src/app/models/provider.model';
import { ProviderCustomizeService } from 'src/app/services/provider-customize.service';
import { ProviderService } from 'src/app/services/provider.service';
import { RegistrationService } from 'src/app/services/registration.service';

@Directive({
  selector: 'button:not([default])',
  standalone: false
})
export class ExtendedButtonDirective implements AfterViewInit, OnDestroy {
  provider!: Provider | null;
  element = this.el.nativeElement;
  classesToIgnore = ['mat-icon-button', 'mat-mdc-icon-button'];
  private observer!: MutationObserver;
  private subscription!: Subscription;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private providerService: ProviderService,
    private registrationService: RegistrationService,
    private providerCustomizeService: ProviderCustomizeService,
  ) {}

  ngAfterViewInit() {
    this.provider = this.registrationService.getProvider();
    if(!this.providerService.isLanus(this.provider?.id as string)) {
      this.checkApplyStyles();

      this.subscription = this.registrationService.provider$.subscribe(
        (provider: Provider | null) => {
          this.provider = provider;
          this.checkApplyStyles();
        }
      );
    }
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    this.subscription?.unsubscribe();
  }

  private checkApplyStyles(): void {
    if (this.provider?.whiteLabel && this.provider?.customButtonColor) {
      this.needApplyStyles() ? this.setStyles() : undefined;
      if (!this.observer) this.observeDisabledChanges();
    } else this.removeStyles();
  }

  private needApplyStyles(): boolean {
    const isMatDialogClose = this.element.hasAttribute('mat-dialog-close');
    const isTertiaryButton = this.element.getAttribute('color') === 'tertiary';
    const isDisabled = this.element.hasAttribute('disabled');
    const hasIgnoredClass = this.classesToIgnore.some((className: string) =>
      this.element.classList.contains(className)
    );
    return (!hasIgnoredClass || isMatDialogClose) && !isTertiaryButton && !isDisabled;
  }

  private setStyles() {
    let bgColor = null;
    const isOutlinedButton =
      this.element.classList.contains('mat-stroked-button') ||
      this.element.classList.contains('mat-mdc-outlined-button');
    const isTextButton =
      this.element.classList.contains('mat-button') ||
      this.element.classList.contains('mat-mdc-button');

    if (isOutlinedButton) {
      bgColor = this.element.style.backgroundColor;
      this.setStyle('border', `1px solid ${this.provider?.customButtonColor}`);
    }
    if (isTextButton) bgColor = '#ffffff';
    else {
      bgColor = this.provider?.customButtonColor;
      this.setStyle('background-color', bgColor as string);
    }

    this.setStyle(
      'color',
      this.providerCustomizeService.getContrastColor(bgColor) as string
    );
  }

  private setStyle(property: string, color: string): void {
    this.renderer.setStyle(
      this.element,
      property,
      color,
      RendererStyleFlags2.Important
    );
  }

  private removeStyles(): void {
    const isOutlinedButton =
      this.element.classList.contains('mat-stroked-button') ||
      this.element.classList.contains('mat-mdc-outlined-button');

    isOutlinedButton
      ? this.removeStyle('border')
      : this.removeStyle('background-color');
    this.removeStyle('color');
  }

  private removeStyle(property: string): void {
    this.renderer.removeStyle(this.element, property);
  }

  // Observe changes when button is enabled and disabled
  private observeDisabledChanges(): void {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'disabled')
          this.needApplyStyles() ? this.setStyles() : this.removeStyles();
      });
    });

    this.observer.observe(this.element, {
      attributeFilter: ['disabled'], // observe only disabled mutation
    });
  }
}
